import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";

import { getAdminPayouts, createPayout, markPayoutPaid } from "../../api/adminApi";

export default function AdminPayouts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [open, setOpen] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [courierId, setCourierId] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");

  const load = async () => {
  setLoading(true);
  setError("");
  try {
    const res = await getAdminPayouts();
    setItems(res.data?.items || []);
  } catch (e) {
    console.error(e);
    const msg =
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      `Failed to load payouts (HTTP ${e?.response?.status || "?"})`;
    setError(msg);
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
  setError("");

  const orderIdNum = orderId ? Number(orderId) : null;
  const courierIdNum = courierId ? Number(courierId) : null;
  const amountNum = Number(amount);

  if (orderId && !Number.isFinite(orderIdNum)) {
    setError("Order ID must be a number (or leave it empty).");
    return;
  }

  if (courierId && !Number.isFinite(courierIdNum)) {
    setError("Courier ID must be a number (or leave it empty).");
    return;
  }

  if (!Number.isFinite(amountNum) || amountNum <= 0) {
    setError("Amount must be a valid number greater than 0.");
    return;
  }

  if (!orderIdNum && !courierIdNum) {
    setError("Provide at least Order ID or Courier ID.");
    return;
  }

  try {
    await createPayout({
      order_id: orderIdNum,
      courier_id: courierIdNum,
      amount: amountNum,
      method: "manual",
      notes: notes || "",
    });

    setOpen(false);
    setOrderId("");
    setCourierId("");
    setAmount("");
    setNotes("");
    await load();
  } catch (e) {
    console.error(e);
    const msg =
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      `Failed to create payout (HTTP ${e?.response?.status || "?"})`;
    setError(msg);
  }
};


  const handleMarkPaid = async (id) => {
    setError("");
    try {
      await markPayoutPaid(id, { method: "manual", notes: "" });
      await load();
    } catch (e) {
      console.error(e);
      setError("Failed to mark payout paid.");
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
        Courier Payouts
      </Typography>
      <Typography sx={{ mb: 2, color: "text.secondary" }}>
        Payout ledger (owed → paid).
      </Typography>

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
        <Button variant="contained" onClick={() => setOpen(true)}>
          Create Payout
        </Button>
        <Button variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
        {loading && <Typography sx={{ color: "text.secondary" }}>Loading…</Typography>}
        {error && <Typography sx={{ color: "error.main" }}>{error}</Typography>}
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Courier</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Paid At</TableCell>
              <TableCell>Paid By</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{p.id}</TableCell>
                <TableCell>{p.order_id || "-"}</TableCell>
                <TableCell>{p.courier_id || "-"}</TableCell>
                <TableCell>{p.amount}</TableCell>
                <TableCell>{p.status}</TableCell>
                <TableCell>{p.paid_at ? new Date(p.paid_at).toLocaleString() : "-"}</TableCell>
                <TableCell>{p.paid_by || "-"}</TableCell>
                <TableCell>{p.method || "-"}</TableCell>
                <TableCell>{p.notes || "-"}</TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={String(p.status).toLowerCase() === "paid"}
                    onClick={() => handleMarkPaid(p.id)}
                  >
                    Mark Paid
                  </Button>
                </TableCell>
              </TableRow>
            ))}

            {!items.length && !loading && (
              <TableRow>
                <TableCell colSpan={10} sx={{ color: "text.secondary" }}>
                  No payouts found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Payout</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
      <TextField
  label="Order ID (optional)"
  type="number"
  value={orderId}
  onChange={(e) => setOrderId(e.target.value)}
/>

<TextField
  label="Courier ID (optional)"
  type="number"
  value={courierId}
  onChange={(e) => setCourierId(e.target.value)}
/>

<TextField
  label="Amount"
  type="number"
  inputProps={{ step: "0.01" }}
  value={amount}
  onChange={(e) => setAmount(e.target.value)}
/>

<TextField
  label="Notes (optional)"
  value={notes}
  onChange={(e) => setNotes(e.target.value)}
/>

        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
